import React, { useEffect, useState } from "react";
import axios from "axios";
import API from "../../../services/interceptor";

// PatientPopup React component
// Props:
// - patientId (string) - id used to fetch patient data from API
// - open (bool) - whether modal is open
// - onClose (fn) - close handler
// Usage:
// <PatientPopup patientId={"69003bd1fb50af31c356d483"} open={open} onClose={() => setOpen(false)} />

export default function SuperPatientProfile({ patientId, open = false, onClose = () => { } }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (!patientId) return setError("Missing patientId");

    const source = axios.CancelToken.source();

    async function fetchPatient() {
      try {
        setLoading(true);
        setError(null);
        // change endpoint as needed
        const res = await API.get(`/api/users/user-all-details/${encodeURIComponent(patientId)}`, {
          // const res = await API.get(`/api/users/user-all-details/69003bd1fb50af31c356d483`, {
          cancelToken: source.token,
        });
        setData(res.data);
      } catch (err) {
        if (!axios.isCancel(err)) setError(err.message || "Failed to fetch patient");
      } finally {
        setLoading(false);
      }
    }

    fetchPatient();
    return () => source.cancel();
  }, [open, patientId]);

  // ----- Helper calculators -----
  function formatCurrency(n) {
    if (n == null) return "₹0";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
  }

  function safeNumber(v) {
    return typeof v === "number" ? v : Number(v) || 0;
  }

  function computeFinancialSummary(patientData) {
    // iterate opdForms and sum billing items (totalCost) and visits amounts
    let totalBilling = 0;
    let totalPaid = 0;
    let visitsCount = 0;

    const opdForms = patientData?.opdForms || [];
    for (const opd of opdForms) {
      const fin = opd?.patientReport?.financials;
      if (!fin) continue;
      // billingItems totalCost or pricePerItem*quantity fallback
      const billingItems = fin.billingItems || [];
      for (const b of billingItems) {
        if (b?.totalCostPerItem != null) totalBilling += safeNumber(b.totalCostPerItem);
        else totalBilling += safeNumber(b.pricePerItem) * safeNumber(b.quantity);
      }

      const visits = fin.visits || [];
      visitsCount += visits.length;
      for (const v of visits) {
        // Only count amount as paid if treatment is done
        if (v.treatmentDone) {
          totalPaid += safeNumber(v.amount);
        }
      }
    }

    const remaining = Math.max(0, totalBilling - totalPaid);
    return { totalBilling, totalPaid, remaining, visitsCount };
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* backdrop */}
      <div className="absolute inset-0 bg-red-500/50" onClick={onClose} />

      <div className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl p-6 z-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold">Patient Details</h2>
            <p className="text-sm text-slate-500">Comprehensive overview and financials</p>
          </div>

          <div className="flex gap-2">

            <button
              onClick={onClose}
              className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
            >
              Close
            </button>
          </div>
        </div>

        <div className="mt-4">
          {loading && <div className="py-6 text-center">Loading patient...</div>}
          {error && <div className="py-6 text-center text-red-600">{error}</div>}

          {!loading && !error && !data && (
            <div className="py-6 text-center text-slate-600">No patient data available</div>
          )}

          {data && (
            <div className="space-y-6">
              {/* Header row with basic info + stats */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-20 w-20 rounded-full bg-blue-100 flex items-center justify-center text-3xl font-bold text-blue-700">
                    {data.userDetails?.name?.split(" ")[0]?.[0] || "P"}
                  </div>
                  <div>
                    <div className="text-lg font-semibold">{data.userDetails?.name}</div>
                    <div className="text-sm text-slate-600">{data.email}</div>
                    <div className="text-sm text-slate-600">{data.userDetails?.contact?.mobile}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full md:w-auto">
                  {/* compute stats */}
                  {(() => {
                    const opdCount = (data.opdForms || []).length;
                    const appointmentCount = (data.appointmentDetails || []).length;
                    const { totalBilling, totalPaid, remaining, visitsCount } = computeFinancialSummary(data);
                    return (
                      <>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="text-xs text-slate-500">OPDs</div>
                          <div className="text-lg font-semibold">{opdCount}</div>
                        </div>

                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="text-xs text-slate-500">Appointments</div>
                          <div className="text-lg font-semibold">{appointmentCount}</div>
                        </div>

                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="text-xs text-slate-500">Visits</div>
                          <div className="text-lg font-semibold">{visitsCount}</div>
                        </div>

                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="text-xs text-slate-500">Remaining</div>
                          <div className="text-lg font-semibold">{formatCurrency(remaining)}</div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Two-column detail area */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: summary cards */}
                <div className="lg:col-span-1 space-y-4">
                  <div className="p-4 border rounded-lg">
                    <div className="text-sm text-slate-500">Personal</div>
                    <div className="mt-2 text-sm">
                      <div><span className="font-medium">DOB:</span> {data.userDetails?.dateOfBirth ? new Date(data.userDetails.dateOfBirth).toLocaleDateString('en-IN') : "-"}</div>
                      <div><span className="font-medium">Gender:</span> {data.userDetails?.gender || "-"}</div>
                      <div><span className="font-medium">Blood Group:</span> {data.medicalDetails?.bloodGroup || "-"}</div>
                      <div><span className="font-medium">Status:</span> {data.status}</div>
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <div className="text-sm text-slate-500">Contact</div>
                    <div className="mt-2 text-sm">
                      <div>{data.userDetails?.contact?.email}</div>
                      <div>{data.userDetails?.contact?.mobile}</div>
                      <div className="text-xs text-slate-400">{data.userDetails?.contact?.address}</div>
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <div className="text-sm text-slate-500">Emergency</div>
                    <div className="mt-2 text-sm">
                      <div>{data.userDetails?.emergencyContact?.name}</div>
                      <div>{data.userDetails?.emergencyContact?.phone}</div>
                    </div>
                  </div>
                </div>

                {/* Middle: OPD list */}
                <div className="lg:col-span-2 space-y-4">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-slate-500">OPD Records</div>
                      <div className="text-xs text-slate-400">Total: {(data.opdForms || []).length}</div>
                    </div>

                    <div className="mt-3 space-y-3">
                      {console.log("data:", data)}
                      {(data.opdForms || []).map((opd) => (
                        <div key={opd._id} className={`p-3 bg-slate-50 rounded-lg 
                        ${opd.isCompleted  ? 'border-l-4 border-green-500' : 'border-l-4 border-red'}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="font-medium">{opd.opdNumber} · {opd.isCompleted?"Open":"Completed"}</div>
                              <div className="text-sm text-slate-600">{opd.checkupInfo}</div>
                            </div>
                            <div className="text-sm text-slate-600">Age: {opd.age}</div>
                          </div>

                          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                            <div>
                              <div className="text-xs text-slate-500">Diagnosis</div>
                              <div>{opd.patientReport?.diagnosis?.primaryDiagnosis || "-"}</div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-500">Treatment</div>
                              <div>{opd.patientReport?.treatmentPlan?.treatmentSuggested || "-"}</div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-500">Billing</div>
                              <div>{formatCurrency(opd.patientReport?.financials?.totalCost || 0)}</div>
                            </div>
                          </div>

                          {/* visits */}
                          {/* {console.log("visits:", opd.patientReport?.financials?.visits)} */}
                          <div className="mt-3 text-sm">
                            <div className="text-xs text-slate-500">Visits</div>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {(opd.patientReport?.financials?.visits || [])
                                .sort((a, b) => new Date(b.date) - new Date(a.date))
                                .map((v) => (
                                  <div key={v._id} className={`px-2 py-1 bg-white border rounded text-xs ${v.treatmentDone ? 'border-green-500' : 'border-red-500'
                                    }`}>
                                    <div>{new Date(v.date).toLocaleDateString('en-IN')} · {v.time}</div>
                                    <div className="text-slate-600">{v.reason} · {formatCurrency(v.amount)}</div>
                                  </div>
                                ))}
                            </div>
                          </div>

                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Appointments */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-slate-500">Appointments</div>
                      <div className="text-xs text-slate-400">{(data.appointmentDetails || []).length} scheduled</div>
                    </div>

                    <div className="mt-3 space-y-2">
                      {(data.appointmentDetails || []).map((a) => (
                        <div key={a._id} className="p-2 bg-slate-50 rounded">
                          <div className="text-sm font-medium">{new Date(a.date).toLocaleDateString('en-IN')} · {a.timeBlock}</div>
                          <div className="text-xs text-slate-600">{a.reason} · {a.status}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Medical summary: allergies / medications */}
                  <div className="p-4 border rounded-lg">
                    <div className="text-sm text-slate-500">Medical Summary</div>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-slate-500">Allergies</div>
                        <div className="mt-2 text-sm">
                          {(data.opdForms || []).flatMap(o => o.patientReport?.examinationFindings?.allergies || []).length === 0 && <div className="text-slate-400">None recorded</div>}
                          {(data.opdForms || []).flatMap(o => o.patientReport?.examinationFindings?.allergies || []).map((al) => (
                            <div key={al._id} className="text-sm">{al.allergen} — {al.reaction} ({al.severity})</div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="text-xs text-slate-500">Medications</div>
                        <div className="mt-2 text-sm">
                          {(data.medicalDetails?.medications || []).length === 0 && <div className="text-slate-400">No meds recorded</div>}
                          {(data.medicalDetails?.medications || []).map((m) => (
                            <div key={m._id} className="text-sm">{m.name || "-"} {m.dosage ? `· ${m.dosage}` : ""} {m.durationDays ? `· ${m.durationDays} days` : ""}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Bottom financial summary */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-slate-500">Financial Summary</div>
                    <div className="mt-1 text-lg font-semibold">{formatCurrency(computeFinancialSummary(data).totalBilling)}</div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <div className="text-xs text-slate-500">Total Paid</div>
                      <div className="font-medium">{formatCurrency(computeFinancialSummary(data).totalPaid)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Remaining</div>
                      <div className="font-medium text-red-600">{formatCurrency(computeFinancialSummary(data).remaining)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Visits</div>
                      <div className="font-medium">{computeFinancialSummary(data).visitsCount}</div>
                    </div>
                  </div>
                </div>

                {/* billing items expanded */}
                <div className="mt-4">
                  <div className="text-sm text-slate-500">Billing Items (by OPD)</div>
                  <div className="mt-3 space-y-2">
                    {(data.opdForms || []).map((opd) => (
                      <div key={opd._id} className="p-2 bg-white border rounded">
                        <div className="text-sm font-medium">{opd.opdNumber} · {formatCurrency(opd.patientReport?.financials?.totalCost || 0)}</div>
                        <div className="mt-1 text-xs text-slate-600">{(opd.patientReport?.financials?.billingItems || []).map(b => `${b.name} (${b.quantity}x) ${formatCurrency(b.totalCostPerItem ?? (b.pricePerItem * b.quantity))}`).join(' · ')}</div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
